import { z } from 'zod';

/**
 * Курсорная пагинация.
 *
 * Не offset: список диалогов пересортировывается на каждом входящем сообщении,
 * и `LIMIT/OFFSET` там показывает то дубли, то пропуски. Курсор — непрозрачная
 * для клиента строка; её формат (пара «сортируемое поле + id») знает только api
 * и вправе менять без согласования с фронтом.
 */

export const PAGINATION_DEFAULT_LIMIT = 30;
export const PAGINATION_MAX_LIMIT = 100;

/** coerce: в query-строке limit всегда приезжает строкой. */
export const limitSchema = z.coerce
  .number()
  .int()
  .min(1)
  .max(PAGINATION_MAX_LIMIT)
  .default(PAGINATION_DEFAULT_LIMIT);

export const cursorSchema = z.string().min(1).max(512);

export const cursorPaginationSchema = z.object({
  cursor: cursorSchema.optional(),
  limit: limitSchema,
});
export type CursorPagination = z.infer<typeof cursorPaginationSchema>;
export type CursorPaginationInput = z.input<typeof cursorPaginationSchema>;

/**
 * Направление обхода. Нужно треду: вверх подгружаются старые сообщения, а при
 * переходе к первому непрочитанному — ещё и новые от точки входа.
 */
export const CURSOR_DIRECTIONS = ['before', 'after'] as const;
export const cursorDirectionSchema = z.enum(CURSOR_DIRECTIONS).default('before');
export type CursorDirection = (typeof CURSOR_DIRECTIONS)[number];

export const scrollPaginationSchema = z.object({
  cursor: cursorSchema.optional(),
  limit: limitSchema,
  direction: cursorDirectionSchema,
});
export type ScrollPagination = z.infer<typeof scrollPaginationSchema>;
export type ScrollPaginationInput = z.input<typeof scrollPaginationSchema>;

/**
 * Страница результата. nextCursor=null — конец списка; отдельный hasMore не
 * нужен, но он есть для читаемости кода на фронте.
 */
export type CursorPage<T> = {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
};

/** Схема страницы для конкретного элемента — для валидации ответов на фронте. */
export function cursorPageSchema<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
  });
}
